import type { Content } from '@prismicio/client'
import { trackWizardEvent, wizardStepName } from '~/utils/wizardAnalytics'

export type WizardRoom = {
  id: string
  type: string
  floor: string
  name: string
  m2: number | null
}

export type WizardDetails = {
  first_name: string
  last_name: string
  email: string
  phone: string
  postcode: string
  house_number: string
  house_addition: string
  street: string
  city: string
  parking: string
  /** Honeypot — must stay empty for real users. */
  company: string
}

export const ROOM_TYPES = [
  'Woonkamer',
  'Slaapkamer',
  'Keuken',
  'Badkamer',
  'Studeerkamer / kantoor',
  'Zolder',
  'Hal / overloop',
  'Overig',
] as const

export const FLOORS = [
  'Begane grond',
  '1e verdieping',
  '2e verdieping',
  '3e verdieping of hoger',
  'Zolder',
  'Kelder',
] as const

export const PARKING_OPTIONS = [
  'Ja, op eigen terrein / oprit',
  'Ja, vrij parkeren in de straat',
  'Beperkt of betaald parkeren',
  'Nee, lastig te parkeren',
  'Weet ik niet',
] as const

export const PLANNING_DAYS = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag'] as const

export const PLANNING_SLOTS = [
  { id: 'morning', label: 'Ochtend', time: '08:00 – 12:00' },
  { id: 'afternoon', label: 'Middag', time: '12:00 – 16:00' },
] as const

export const TOTAL_STEPS = 5

const STORE_KEY = 'proair_wizard_v1'

/**
 * Pre-seed the wizard's saved state with partial customer details so it
 * hydrates pre-filled the next time the wizard page mounts. Used by the
 * OffertaHeroForm hand-off so a customer doesn't retype their info.
 */
export function seedWizardDetails(partial: Partial<WizardDetails>): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(STORE_KEY)
    const existing = raw ? JSON.parse(raw) : {}
    const next = {
      ...existing,
      step: 1,
      details: { ...emptyDetails(), ...(existing.details || {}), ...partial },
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(next))
  } catch {
    // ignore quota / private mode
  }
}

function emptyDetails(): WizardDetails {
  return {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    postcode: '',
    house_number: '',
    house_addition: '',
    street: '',
    city: '',
    parking: '',
    company: '',
  }
}

function makeRoomId(): string {
  return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

export function newRoom(type: string = 'Woonkamer'): WizardRoom {
  return {
    id: makeRoomId(),
    type,
    floor: 'Begane grond',
    name: '',
    m2: null,
  }
}

export const useOfferteWizard = () => {
  const step = useState<number>('wizard-step', () => 1)
  const pkg = useState<string | null>('wizard-pkg', () => null)
  const details = useState<WizardDetails>('wizard-details', () => emptyDetails())
  const rooms = useState<WizardRoom[]>('wizard-rooms', () => [newRoom()])
  const planningSlots = useState<string[]>('wizard-planning-slots', () => [])
  const planningNoPreference = useState<boolean>('wizard-planning-no-pref', () => false)
  const consent = useState<boolean>('wizard-consent', () => false)
  const fieldErrors = useState<Record<string, string>>('wizard-errors', () => ({}))
  const submitting = useState<boolean>('wizard-submitting', () => false)
  const submitted = useState<boolean>('wizard-submitted', () => false)

  const totalM2 = computed(() =>
    rooms.value.reduce((sum, r) => sum + (typeof r.m2 === 'number' ? r.m2 : 0), 0),
  )

  function setRecommendedPackage(packages: Content.AircoPackageDocument[] | null) {
    recommendedPackages.value = packages ?? []
  }

  const recommendedPackages = useState<Content.AircoPackageDocument[]>(
    'wizard-rec-pool',
    () => [],
  )

  const recommendedPackage = computed<Content.AircoPackageDocument | null>(() => {
    const m2 = totalM2.value
    const roomCount = rooms.value.length
    if (!m2 || !recommendedPackages.value.length) return null
    return (
      recommendedPackages.value.find((p) => {
        const limits = p.data as unknown as { max_m2?: number | null; max_rooms?: number | null }
        const m2Ok = typeof limits.max_m2 === 'number' && limits.max_m2 >= m2
        const roomsOk =
          typeof limits.max_rooms !== 'number' || limits.max_rooms >= roomCount
        return m2Ok && roomsOk
      }) ?? null
    )
  })

  function clearError(name: string) {
    if (!fieldErrors.value[name]) return
    const next: Record<string, string> = {}
    for (const k of Object.keys(fieldErrors.value)) {
      if (k !== name) next[k] = fieldErrors.value[k]
    }
    fieldErrors.value = next
  }

  function validateDetails(): boolean {
    const errs: Record<string, string> = {}
    const d = details.value
    if (!d.first_name.trim()) errs.first_name = 'Vul je voornaam in'
    if (!d.last_name.trim()) errs.last_name = 'Vul je achternaam in'
    if (!d.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) {
      errs.email = 'Vul een geldig e-mailadres in'
    }
    if (d.phone.replace(/[^0-9]/g, '').length < 8) {
      errs.phone = 'Vul een geldig telefoonnummer in'
    }
    if (!d.postcode.trim()) errs.postcode = 'Vul je postcode in'
    if (!d.house_number.trim()) errs.house_number = 'Vul je huisnummer in'
    if (!d.parking) errs.parking = 'Maak een keuze'
    fieldErrors.value = errs
    return Object.keys(errs).length === 0
  }

  function validateRooms(): boolean {
    const errs: Record<string, string> = {}
    if (rooms.value.length === 0) {
      errs._rooms = 'Voeg minimaal één ruimte toe'
      fieldErrors.value = errs
      return false
    }
    rooms.value.forEach((r) => {
      if (typeof r.m2 !== 'number' || r.m2 <= 0) {
        errs[`room_${r.id}_m2`] = 'Vul de oppervlakte in'
      }
    })
    fieldErrors.value = errs
    return Object.keys(errs).length === 0
  }

  function validatePlanning(): boolean {
    if (planningNoPreference.value || planningSlots.value.length > 0) {
      fieldErrors.value = {}
      return true
    }
    fieldErrors.value = { _planning: 'Selecteer minimaal één dagdeel of kies "geen voorkeur"' }
    return false
  }

  function togglePlanningSlot(day: string, slotId: string) {
    const key = `${day}:${slotId}`
    if (planningSlots.value.includes(key)) {
      planningSlots.value = planningSlots.value.filter((k) => k !== key)
    } else {
      planningSlots.value = [...planningSlots.value, key]
      planningNoPreference.value = false
    }
    if (fieldErrors.value._planning) {
      fieldErrors.value = {}
    }
    persist()
  }

  function setPlanningNoPreference(v: boolean) {
    planningNoPreference.value = v
    if (v) planningSlots.value = []
    if (fieldErrors.value._planning) {
      fieldErrors.value = {}
    }
    persist()
  }

  function trackStepView(n: number, direction: 'next' | 'back' | 'jump' | 'initial') {
    trackWizardEvent('wizard_step_view', {
      step_number: n,
      step_name: wizardStepName(n),
      direction,
    })
  }

  function goNext() {
    const from = step.value
    if (from === 1) {
      if (!pkg.value) {
        fieldErrors.value = { _pkg: 'Kies een pakket of vraag advies' }
        return
      }
    } else if (from === 2) {
      if (!validateDetails()) return
    } else if (from === 3) {
      if (!validateRooms()) return
    } else if (from === 4) {
      if (!validatePlanning()) return
    }
    fieldErrors.value = {}
    trackWizardEvent('wizard_step_complete', {
      step_number: from,
      step_name: wizardStepName(from),
    })
    if (from < TOTAL_STEPS) {
      step.value = from + 1
      persist()
      scrollToTop()
      trackStepView(step.value, 'next')
    }
  }

  function goBack() {
    if (step.value > 1) {
      step.value--
      fieldErrors.value = {}
      persist()
      scrollToTop()
      trackStepView(step.value, 'back')
    }
  }

  function goTo(n: number) {
    if (n >= 1 && n <= TOTAL_STEPS && n !== step.value) {
      step.value = n
      fieldErrors.value = {}
      persist()
      scrollToTop()
      trackStepView(step.value, 'jump')
    }
  }

  function addRoom(type?: string) {
    rooms.value = [...rooms.value, newRoom(type)]
    persist()
  }

  function removeRoom(id: string) {
    rooms.value = rooms.value.filter((r) => r.id !== id)
    persist()
  }

  function updateRoom(id: string, patch: Partial<WizardRoom>) {
    rooms.value = rooms.value.map((r) => (r.id === id ? { ...r, ...patch } : r))
    if (patch.m2 !== undefined) clearError(`room_${id}_m2`)
    persist()
  }

  async function submit(): Promise<{ ok: boolean; error?: string }> {
    if (!consent.value) {
      fieldErrors.value = { consent: 'Vink dit aan om te versturen' }
      trackWizardEvent('wizard_submit_error', { reason: 'consent_missing' })
      return { ok: false, error: 'consent' }
    }
    submitting.value = true
    trackWizardEvent('wizard_submit_attempt', {
      package: pkg.value || 'none',
      total_m2: totalM2.value,
      rooms_count: rooms.value.length,
    })
    try {
      const payload = {
        pkg: pkg.value,
        recommended_pkg: recommendedPackage.value?.uid ?? null,
        details: details.value,
        rooms: rooms.value,
        planning_slots: planningSlots.value,
        planning_no_preference: planningNoPreference.value,
        consent: consent.value,
        total_m2: totalM2.value,
      }
      const res = await $fetch<{ ok: boolean; error?: string }>(
        '/api/quote-wizard',
        { method: 'POST', body: payload },
      )
      if (res.ok) {
        submitted.value = true
        const value =
          typeof recommendedPackage.value?.data?.from_price === 'number'
            ? recommendedPackage.value.data.from_price
            : undefined
        trackWizardEvent('generate_lead_wizard', {
          currency: 'EUR',
          value,
          package: pkg.value || 'none',
          recommended_pkg: recommendedPackage.value?.uid || 'none',
          total_m2: totalM2.value,
          rooms_count: rooms.value.length,
        })
        try {
          localStorage.removeItem(STORE_KEY)
        } catch {
          // ignore
        }
        return { ok: true }
      }
      trackWizardEvent('wizard_submit_error', { reason: res.error || 'unknown' })
      return { ok: false, error: res.error || 'unknown' }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'request failed'
      trackWizardEvent('wizard_submit_error', { reason: 'request_failed' })
      return { ok: false, error: message }
    } finally {
      submitting.value = false
    }
  }

  function persist() {
    if (typeof window === 'undefined') return
    try {
      const snapshot = {
        step: step.value,
        pkg: pkg.value,
        details: details.value,
        rooms: rooms.value,
        planning_slots: planningSlots.value,
        planning_no_preference: planningNoPreference.value,
        consent: consent.value,
      }
      localStorage.setItem(STORE_KEY, JSON.stringify(snapshot))
    } catch {
      // ignore localStorage failures (private mode, quota, etc.)
    }
  }

  function hydrate() {
    if (typeof window === 'undefined') return
    try {
      const raw = localStorage.getItem(STORE_KEY)
      if (!raw) return
      const s = JSON.parse(raw)
      if (s && typeof s === 'object') {
        if (typeof s.step === 'number' && s.step >= 1 && s.step <= TOTAL_STEPS) step.value = s.step
        if (typeof s.pkg === 'string' || s.pkg === null) pkg.value = s.pkg
        if (s.details && typeof s.details === 'object') {
          details.value = { ...emptyDetails(), ...s.details }
        }
        if (Array.isArray(s.rooms) && s.rooms.length) {
          rooms.value = s.rooms.map((r: WizardRoom) => ({
            id: r.id || makeRoomId(),
            type: r.type || 'Woonkamer',
            floor: r.floor || 'Begane grond',
            name: r.name || '',
            m2: typeof r.m2 === 'number' ? r.m2 : null,
          }))
        }
        if (Array.isArray(s.planning_slots)) {
          planningSlots.value = s.planning_slots.filter((x: unknown) => typeof x === 'string')
        }
        planningNoPreference.value = !!s.planning_no_preference
        consent.value = !!s.consent
      }
    } catch {
      // ignore localStorage failures (private mode, quota, etc.)
    }
  }

  function resetWizard() {
    step.value = 1
    pkg.value = null
    details.value = emptyDetails()
    rooms.value = [newRoom()]
    planningSlots.value = []
    planningNoPreference.value = false
    consent.value = false
    fieldErrors.value = {}
    submitted.value = false
    try {
      localStorage.removeItem(STORE_KEY)
    } catch {
      // ignore
    }
  }

  function scrollToTop() {
    if (typeof window === 'undefined') return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return {
    step,
    pkg,
    details,
    rooms,
    planningSlots,
    planningNoPreference,
    consent,
    fieldErrors,
    submitting,
    submitted,
    totalM2,
    recommendedPackage,
    setRecommendedPackage,
    clearError,
    goNext,
    goBack,
    goTo,
    addRoom,
    removeRoom,
    updateRoom,
    togglePlanningSlot,
    setPlanningNoPreference,
    submit,
    persist,
    hydrate,
    resetWizard,
  }
}
